import React, { useState, useEffect } from "react";
import { Button, Divider, Drawer, Menu } from "antd";
import { MenuOutlined } from "@ant-design/icons";
import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/router";
import SignUpNotifyFormModal from "@/components/LandingPage/SignUpNotifyForm";
import IseeLogoIcon from "@/public/images/Isee_Logo.svg";
import { Exam } from "@/enums/exam.enum";
import { IseeLevel } from "@/enums/isee-level.enum";

const Header: React.FC = () => {
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [signUpNotify, setSignUpNotify] = useState<boolean>(false);
  const router = useRouter();
  const showDrawer = () => setDrawerVisible(true);
  const closeDrawer = () => setDrawerVisible(false);

  const handleSignUpToNotify = () => {
    setSignUpNotify(!signUpNotify);
  };

  const handleAnchorClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string
  ) => {
    e.preventDefault();

    if (href.startsWith("#")) {
      const targetId = href.substring(1);

      if (router.pathname !== "/") {
        router.push({
          pathname: "/",
          query: { scrollTo: targetId },
        });
      } else {
        scrollToSection(targetId);
        window.history.pushState(null, "", href);
      }
    } else {
      router.push(href);
    }
  };
  const scrollToSection = (id: string) => {
    const targetElement = document.getElementById(id);
    if (targetElement) {
      const navbarHeight = document.querySelector("header")?.offsetHeight || 0;
      window.scrollTo({
        top: targetElement.offsetTop - navbarHeight,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    if (router.pathname === "/" && router.query.scrollTo) {
      const id = router.query.scrollTo as string;
      const timer = setTimeout(() => scrollToSection(id), 100);
      return () => clearTimeout(timer);
    }
    if (router.pathname === "/" && window.location.hash) {
      const id = window.location.hash.substring(1);
      const timer = setTimeout(() => scrollToSection(id), 100);
      return () => clearTimeout(timer);
    }
  }, [router.query.scrollTo, router.pathname]);

  const examInfo = {
    exam: Exam.ISEE,
    examLevel: IseeLevel.MIDDLE_LEVEL,
  };

  const getFormProps = () => {
    return {
      title: "Sign Up to Notify",
      buttonText: "Sign Up to Notify",
      formType: "join",
      examInfo,
    };
  };

  return (
    <header className="sticky top-0 z-50 bg-white py-3 px-4 shadow-sm">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <Link href="/" className="flex items-center">
          <Image
            src={IseeLogoIcon}
            alt="StedPrep Logo"
            width={60}
            height={60}
            priority
          />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center justify-between w-full ml-10">
          <nav className="flex-1 flex justify-center space-x-10">
            <a
              href="#stedprep-exam-success"
              onClick={(e) => handleAnchorClick(e, "#stedprep-exam-success")}
              className="text-gray-700 hover:text-[#0077B6] font-medium hover:underline hover:underline-offset-4"
            >
              How it Works
            </a>
            <a
              href="#stedprep-partner-with-us"
              onClick={(e) => handleAnchorClick(e, "#stedprep-partner-with-us")}
              className="text-gray-700 hover:text-[#0077B6] font-medium hover:underline hover:underline-offset-4"
            >
              Partner With Us
            </a>
            <a
              href="#stedprep-how-was-it-born"
              onClick={(e) => handleAnchorClick(e, "#stedprep-how-was-it-born")}
              className="text-gray-700 hover:text-[#0077B6] font-medium hover:underline hover:underline-offset-4"
            >
              About Us
            </a>
            <Link
              href="/contact-us"
              className="text-gray-700 hover:text-[#0077B6] font-medium hover:underline hover:underline-offset-4"
            >
              Contact Us
            </Link>
          </nav>

          <div className="flex items-center">
            {/* Sign In Button */}
            <a
              href="https://app.stedprep.com/login"
              target="_blank"
              rel="noopener noreferrer"
              className="mr-4"
            >
              <Button
                htmlType="button"
                className="bg-white text-[#2D68C4] border-[#2D68C4] border rounded px-5 h-9 font-medium hover:bg-[#f0f7ff] hover:border-[#005f92] hover:text-[#005f92]"
              >
                Sign In
              </Button>
            </a>
            {/* Free Diagnostic Test Button */}
            <a
              href="https://app.stedprep.com/register"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                htmlType="button"
                type="primary"
                className="bg-[#2D68C4] hover:bg-[#005f92] border-none rounded px-5 h-9 font-medium"
              >
                Free Diagnostic Test
              </Button>
            </a>
          </div>
        </div>

        {/* Mobile Hamburger */}
        <div className="md:hidden">
          <Button type="text" icon={<MenuOutlined />} onClick={showDrawer} />
        </div>
      </div>

      {/* Drawer for Mobile Nav */}
      <Drawer
        placement="right"
        onClose={closeDrawer}
        open={drawerVisible}
        title={
          <Link href="/" className="flex items-center">
            <Image
              src={IseeLogoIcon}
              alt="StedPrep Logo"
              width={60}
              height={60}
              priority
            />
          </Link>
        }
      >
        <Menu mode="vertical" selectable={false} onClick={closeDrawer}>
          <Menu.Item key="how-it-works">
            <a
              href="#stedprep-exam-success"
              onClick={(e) => {
                handleAnchorClick(e, "#stedprep-exam-success");
                closeDrawer();
              }}
              className="hover:underline hover:underline-offset-4"
            >
              How it Works
            </a>
          </Menu.Item>
          <Menu.Item key="partner-with-us">
            <a
              href="#stedprep-partner-with-us"
              onClick={(e) => {
                handleAnchorClick(e, "#stedprep-partner-with-us");
                closeDrawer();
              }}
              className="hover:underline hover:underline-offset-4"
            >
              Partner With Us
            </a>
          </Menu.Item>
          <Menu.Item key="about-us">
            <a
              href="#stedprep-how-was-it-born"
              onClick={(e) => {
                handleAnchorClick(e, "#stedprep-how-was-it-born");
                closeDrawer();
              }}
              className="hover:underline hover:underline-offset-4"
            >
              About Us
            </a>
          </Menu.Item>
          <Menu.Item key="contact-us">
            <Link
              href="/contact-us"
              className="text-gray-700 hover:text-[#0077B6] font-medium hover:underline hover:underline-offset-4"
            >
              Contact Us
            </Link>
          </Menu.Item>

          <Divider className="mt-0 mb-0" />
          <Menu.Item key="sign-in">
            {/* Sign In Button */}
            <a
              href="https://app.stedprep.com/login"
              target="_blank"
              rel="noopener noreferrer"
              className="block mb-3"
            >
              <Button
                htmlType="button"
                className="bg-white text-[#2D68C4] border-[#2D68C4] border rounded px-5 h-9 font-medium w-full hover:bg-[#f0f7ff] hover:border-[#005f92] hover:text-[#005f92]"
              >
                Sign In
              </Button>
            </a>
          </Menu.Item>
          <Menu.Item key="join-now">
            {/* Free Diagnostic Test Button */}
            <a
              href="https://app.stedprep.com/register"
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <Button
                htmlType="button"
                type="primary"
                className="bg-[#2D68C4] hover:bg-[#005f92] w-full border-none rounded px-5 h-9 font-medium"
              >
                Free Diagnostic Test
              </Button>
            </a>
          </Menu.Item>
        </Menu>
      </Drawer>
      {signUpNotify && (
        <SignUpNotifyFormModal
          signUpNotify={signUpNotify}
          handleSignUpToNotify={handleSignUpToNotify}
          {...getFormProps()}
        />
      )}
    </header>
  );
};

export default Header;
